import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Button, Card, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { getClimatePatternChoices } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import type { Field } from '/@/types/field';

const NOT_AVAILABLE = '-';

/**
 * The four figures across the top of the result page: the small title the field-detail cards use,
 * sitting tight above the figure, with the rounded corner the rest of the result page has.
 */
const CARD_PROPS = { titleVariant: 'subtitle1', spacing: 0.5, sx: { borderRadius: '4px' } } as const;

/** One labelled figure inside the phone's collapsible card. */
function DetailRow({ label, value, detail }: { label: string; value: string; detail?: string }) {
    return (
        <Stack spacing={0.5}>
            <Typography variant='caption' color='primary.mui.inactive'>
                {label}
            </Typography>
            <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
                <Typography variant='body1' fontWeight={700} color='primary'>
                    {value}
                </Typography>
                {detail && (
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {detail}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

/**
 * The phone layout: one card leading with the field and planting date — what identifies the
 * recommendation — and the rest folded away behind a toggle, rather than the four cards the
 * desktop shows side by side.
 */
function CompactGeneralData({
    grower,
    hideGrower,
    field,
    plantingDate,
    area,
    yieldLabel,
    environmentDetail,
}: {
    grower?: { name?: string; email?: string };
    hideGrower?: boolean;
    field?: Field;
    plantingDate?: string;
    area?: string;
    yieldLabel: string;
    environmentDetail?: string;
}) {
    const [expanded, setExpanded] = useState(false);

    return (
        <Card elevation={0} variant='outlined' sx={{ borderRadius: '4px', p: 2 }}>
            <Stack spacing={2}>
                <Stack direction='row' spacing={2} justifyContent='space-between' alignItems='flex-start'>
                    <Stack flex={1} minWidth={0} spacing={0.5}>
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_('Lote')}
                        </Typography>
                        <Typography variant='h6' fontWeight={700} color='primary'>
                            {field?.name || NOT_AVAILABLE}
                        </Typography>
                        {area && (
                            <Typography variant='body2' color='primary.mui.inactive'>
                                {area}
                            </Typography>
                        )}
                    </Stack>
                    <Stack flex={1} minWidth={0} spacing={0.5}>
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_('Fecha de siembra')}
                        </Typography>
                        <Typography variant='h6' fontWeight={700} color='primary'>
                            {plantingDate ? dayjs(plantingDate).format('DD/MM/YYYY') : NOT_AVAILABLE}
                        </Typography>
                    </Stack>
                </Stack>

                <Button
                    variant='text'
                    onClick={() => setExpanded((open) => !open)}
                    endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                    sx={{ alignSelf: 'flex-start', p: 0, textTransform: 'uppercase', color: 'primary.onSurface' }}
                    data-testid='general-data-toggle'
                >
                    {expanded ? _('Ocultar detalles') : _('Ver detalles')}
                </Button>

                {expanded && (
                    <Stack spacing={2}>
                        {!hideGrower && (
                            <DetailRow
                                label={_('Productor')}
                                value={grower?.name || NOT_AVAILABLE}
                                detail={grower?.email}
                            />
                        )}
                        <DetailRow label={_('Ambiente')} value={yieldLabel} detail={environmentDetail} />
                        {field?.location && <DetailRow label={_('Ubicación')} value={field.location} />}
                    </Stack>
                )}
            </Stack>
        </Card>
    );
}

/** Grower, field, planting date and environment — the inputs the recommendation was built on. */
function GeneralData({
    grower,
    hideGrower,
    field,
    plantingDate,
    environment,
    climatePattern,
    soilMoisture,
    crop = 'corn',
    compact,
}: {
    grower?: { name?: string; email?: string };
    /** Own-node recommendations have no grower to name, so the card drops out entirely. */
    hideGrower?: boolean;
    field?: Field;
    plantingDate?: string;
    environment?: number;
    climatePattern?: number;
    soilMoisture?: number;
    crop?: 'corn' | 'soy';
    /** Phone layout: one collapsible card instead of the four side-by-side ones. */
    compact?: boolean;
}) {
    const units = useUnitProvider();

    const area = field?.area ? convertAreaFromM2(field.area, units).toString() : undefined;
    const yieldValue = environment !== undefined ? units.toYield(environment, crop, 0) : undefined;
    const climatePatternLabel = getClimatePatternChoices().find(
        (choice: { value: number; label: string }) => choice.value === climatePattern
    )?.label;
    const environmentDetail = [
        climatePatternLabel,
        soilMoisture !== undefined ? _('{{value}}% humedad', { value: soilMoisture }) : undefined,
    ]
        .filter(Boolean)
        .join(' - ');

    const yieldLabel = yieldValue ? `${yieldValue.localeValue} ${yieldValue.unit}` : NOT_AVAILABLE;

    if (compact) {
        return (
            <CompactGeneralData
                grower={grower}
                hideGrower={hideGrower}
                field={field}
                plantingDate={plantingDate}
                area={area}
                yieldLabel={yieldLabel}
                environmentDetail={environmentDetail || undefined}
            />
        );
    }

    // The wide row sizes itself to the cards it actually has: dropping the grower from a fixed
    // four-column grid would leave the row a column short of the page width.
    const cardCount = hideGrower ? 3 : 4;

    return (
        <Stack
            display='grid'
            gap={2}
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${cardCount}, 1fr)` }}
            alignItems='stretch'
        >
            {!hideGrower && (
                <DashboardCard
                    {...CARD_PROPS}
                    title={_('Productor')}
                    number={grower?.name || NOT_AVAILABLE}
                    subtitle={grower?.email}
                />
            )}
            <DashboardCard
                {...CARD_PROPS}
                title={_('Lote')}
                number={field?.name || NOT_AVAILABLE}
                subtitle={[area, field?.location].filter(Boolean).join(' - ') || undefined}
            />
            <DashboardCard
                {...CARD_PROPS}
                title={_('Fecha de siembra')}
                number={plantingDate ? dayjs(plantingDate).format('DD/MM/YYYY') : NOT_AVAILABLE}
            />
            <DashboardCard
                {...CARD_PROPS}
                title={_('Ambiente')}
                number={yieldLabel}
                subtitle={environmentDetail || undefined}
            />
        </Stack>
    );
}

export default GeneralData;
